import React, { useState } from 'react';
import { HeartHandshake, CheckCircle2, Flame } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../lib/api';

const PrayerRequestForm = () => {
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [state, setState] = useState('idle');
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) {
      setErr('Say a little more so we can pray with you.');
      return;
    }
    setState('loading');
    setErr('');
    try {
      await api.prayer({
        message: message.trim(),
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        urgent,
      });
      setState('success');
    } catch (e2) {
      setErr(e2.message || 'Something went wrong. Please try again.');
      setState('error');
    }
  };

  const reset = () => {
    setMessage('');
    setName('');
    setEmail('');
    setUrgent(false);
    setState('idle');
    setErr('');
  };

  return (
    <section id="prayer" className="relative py-20 bg-gradient-to-b from-[#0a0a0a] via-[#0f0f0f] to-[#0a0a0a]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-1 mb-4">
            <HeartHandshake className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-bold text-xs tracking-wider">PRAYER & SUPPORT</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            You do not have to carry it alone
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto leading-relaxed">
            If you are in a hard place, send us a note. We will pray with you.
            Anonymous is welcome. Nothing you share is public.
          </p>
        </div>

        <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#D4AF37]/20 rounded-3xl p-6 md:p-10">
          {state === 'success' ? (
            <div className="text-center py-6" data-testid="prayer-success">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#FFD700] flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-[#0a0a0a]" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
                We received you
              </h3>
              <p className="text-gray-300 max-w-lg mx-auto mb-6 leading-relaxed">
                Your request is with us now. We will be praying and will follow up if you left an email.
                You are seen. You are not alone.
              </p>
              <Button
                onClick={reset}
                data-testid="prayer-send-another"
                variant="outline"
                className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold"
              >
                Send another request
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="prayer-form">
              <div>
                <label className="block text-xs font-bold text-gray-400 mb-2 tracking-wider">
                  WHAT ARE YOU CARRYING? (REQUIRED)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="prayer-message"
                  required
                  rows={5}
                  placeholder="Say as little or as much as you need to."
                  className="w-full bg-[#0a0a0a] border border-[#D4AF37]/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none rounded-xl px-4 py-3 text-white placeholder:text-gray-600 leading-relaxed"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 mb-2 tracking-wider">
                    FIRST NAME (OPTIONAL)
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="prayer-name"
                    placeholder="Or leave blank to stay anonymous"
                    className="w-full bg-[#0a0a0a] border border-[#D4AF37]/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none rounded-lg px-4 py-3 text-white placeholder:text-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 mb-2 tracking-wider">
                    EMAIL FOR REPLY (OPTIONAL)
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="prayer-email"
                    placeholder="Only if you want us to write back"
                    className="w-full bg-[#0a0a0a] border border-[#D4AF37]/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none rounded-lg px-4 py-3 text-white placeholder:text-gray-600"
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={urgent}
                  onChange={(e) => setUrgent(e.target.checked)}
                  data-testid="prayer-urgent"
                  className="w-5 h-5 accent-[#E63946] cursor-pointer"
                />
                <span className="flex items-center gap-2 text-gray-300 group-hover:text-white transition-colors">
                  <Flame className="w-4 h-4 text-[#E63946]" />
                  Mark as urgent
                </span>
              </label>

              {err && <p className="text-[#E63946] text-sm" data-testid="prayer-error">{err}</p>}

              <Button
                type="submit"
                disabled={state === 'loading'}
                data-testid="prayer-submit"
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold py-6 text-lg transition-all duration-300"
              >
                {state === 'loading' ? 'SENDING...' : 'SEND WITH LOVE'}
              </Button>

              <p className="text-xs text-gray-500 text-center leading-relaxed">
                If you are in immediate crisis, please call or text{' '}
                <a href="tel:988" className="text-[#D4AF37] font-bold hover:underline">988</a>{' '}
                (US Suicide &amp; Crisis Lifeline). We are not an emergency service.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default PrayerRequestForm;
